import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Params } from '@angular/router';
import { ActivityDto, AssetDto, GalleryDto } from '@picflow/types';
import { defaultColorLabels } from 'src/app/configs/standard-presets';
import { filterAssets } from 'src/app/gallery-shared/lib/filter-assets';
import { parseAssetsFilters } from 'src/app/gallery-shared/lib/parse-assets-filter';
import { SortAssets } from 'src/app/gallery-shared/lib/sort-assets-sorting-and-grouping';
import { getPermissions } from 'src/app/lib/get-allowed-feedback-options';
import { ActivityService } from 'src/app/services/activity.service';
import { AssetService } from 'src/app/services/asset.service';
import { SectionService } from 'src/app/services/section.service';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';
import { DisplayGuestEmailDto, DisplayUserEmailDto } from 'src/app/services/visitor.service';

@Injectable({
  providedIn: 'root',
})
export class AssetPageDataService {
  constructor(
    private assetService: AssetService,
    private sectionService: SectionService,
    private storage: StorageService,
    private state: StateService,
    private activityService: ActivityService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  private async getAssets(galleryId: string): Promise<AssetDto[]> {
    if (isPlatformServer(this.platformId)) return [];
    const assets = await this.assetService.getAssetsForGallery(galleryId);
    return assets;
  }

  private async getSections(galleryId: string) {
    if (isPlatformServer(this.platformId)) return [];
    return this.sectionService.getSections(galleryId);
  }

  private async getAsset(assetId: string, galleryId: string) {
    try {
      return await this.assetService.getAsset(assetId, galleryId);
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
    }
  }

  public async get(gallery: GalleryDto, assetId: string, queryParams: Params) {
    const preferences = this.storage.get('advanced-mode-preferences');
    const sorting = preferences?.sortingAndGrouping || 'auto';
    const [assets, sections] = await Promise.all([this.getAssets(gallery.id), this.getSections(gallery.id)]);

    let colorLabels = gallery.color_labels;
    const permissions = getPermissions(gallery, this.state.plan);
    if (!permissions.asset.customLabels) colorLabels = defaultColorLabels;

    const filters = parseAssetsFilters(queryParams.filters) || [];

    const people = [];
    if (filters.some(f => f.key === 'people')) {
      await this.getGalleryPeople(gallery.id, assets, people);
    }

    const filteredAssets = SortAssets(
      filterAssets(assets, filters, queryParams.search, false, people),
      sorting,
      sections,
      colorLabels
    );

    const asset = await this.getAsset(assetId, gallery.id);
    if (isPlatformServer(this.platformId)) return { asset, assets: filteredAssets, sections };
    if (!asset) return;
    return { asset, assets: filteredAssets, sections };
  }

  public async getGalleryPeople(
    galleryId: string,
    assets: AssetDto[],
    people: {
      userOrGuest: DisplayUserEmailDto | DisplayGuestEmailDto;
      assetIds: string[];
      type: 'user' | 'guest';
    }[] = []
  ) {
    const activities = await this.getAllActivitiesFiltered(galleryId);
    people.length = 0;
    activities.forEach(activity => {
      if (activity.type === 'asset-like.created') {
        const asset = assets.find(img => img.id === activity.context_asset);
        if (asset) {
          if (asset.likes === 0) {
            return;
          }
          if (asset.likes === 1) {
            const isCurrentUser = this.state.user && this.state.user.id === activity.user?.['id'];

            if (isCurrentUser && !asset['is_liked']) {
              return;
            }

            if (!isCurrentUser && asset['is_liked']) {
              return;
            }
          }
        }
      }

      if (activity.type === 'comment.created') {
        const asset = assets.find(img => img.id === activity.context_asset);
        if (asset && asset.text_comments_total_count === 0) return;
      }

      if (activity.type === 'asset-annotation.created') {
        const asset = assets.find(img => img.id === activity.context_asset);
        if (asset && asset.annotation_comments_total_count === 0) return;
      }

      if (activity.user) {
        if (!people.find(p => p.userOrGuest.id === activity.user['id'])) {
          people.push({ userOrGuest: <DisplayUserEmailDto>activity.user, assetIds: [], type: 'user' });
        }
      }
      if (activity.guest) {
        if (!people.find(p => p.userOrGuest.id === activity.guest['id'])) {
          people.push({ userOrGuest: <DisplayGuestEmailDto>activity.guest, assetIds: [], type: 'guest' });
        }
      }

      const person = people.find(p => {
        if (activity.user) return p.userOrGuest.id === activity.user?.['id'];
        if (activity.guest) return p.userOrGuest.id === activity.guest?.['id'];
      });

      if (person) {
        person.assetIds.push(<string>activity.context_asset);
      }
    });

    return people;
  }

  private async getAllActivitiesFiltered(galleryId: string) {
    const activities = await this.getAllActivities(galleryId);
    const filtered: ActivityDto[] = [];

    activities.forEach(activity => {
      const activityPersonId = activity.user?.['id'] ?? activity.guest?.['id'];

      const duplicate = filtered.find(a => {
        if (a.context_asset !== activity.context_asset) return false;
        if (['asset-flag.created', 'asset-color-label.created', 'asset-status.created'].includes(activity.type)) {
          return a.type === activity.type;
        }

        const aPersonId = a.user?.['id'] ?? a.guest?.['id'];
        if (aPersonId !== activityPersonId) return false;
        return activity.type.split('.')[0] === a.type.split('.')[0];
      });
      if (duplicate) return;
      filtered.push(activity);
    });

    return filtered.filter(activity => {
      if (activity.type === 'asset-status.created') {
        return activity.data.object['status'] !== 'no_status';
      }
      if (activity.type === 'asset-color-label.created') {
        return activity.data.object['color_label'] !== null;
      }
      if (activity.type.split('.')[1] === 'deleted') {
        return false;
      }
      return true;
    });
  }

  private async getAllActivities(galleryId: string, page = 0): Promise<ActivityDto[]> {
    const resp = await this.activityService.getGalleryFeedbackActivity(galleryId, page, 100);
    if (resp.isMore) {
      const more = await this.getAllActivities(galleryId, page + 1);
      return [...resp.activities, ...more];
    }
    return resp.activities;
  }
}
